import { Cable } from "@anycable/web";
import { Dayjs } from "dayjs";
import { FeatureCollection } from "geojson";
import React from "react";
import { MapRef } from "react-map-gl/mapbox";
import { Area } from "../models/Area";
import { CalendarEvent } from "../models/CalendarEvent";
import { Delivery } from "../models/Delivery";
import { Dropoff } from "../models/Dropoff";
import { DropoffGate } from "../models/DropoffGate";
import { Equipment } from "../models/Equipment";
import { Gate } from "../models/Gate";
import { GateToDropoffRoute } from "../models/GateToDropoffRoute";
import { HaulRoute } from "../models/HaulRoute";
import { MapObject } from "../models/MapObject";
import { WaypointStep } from "../models/WaypointStep";
import PageContentType from "../PageContentType";
import Permission from "../Permission";
import TeamInfo from "../teamInfo";
import UserInfo from "../userInfo";
import CalendarEventsFilterInfo from "./calendarEventsFilterInfo";
import { HoveredDropoffGatePair, PendingGateToDropoffRoute } from "./useMapInfo";

export type GlobalStoreContextType = {
    userInfo: UserInfo | null;
    setUserInfo(userInfo: UserInfo): Promise<boolean>;
    loggedIn: boolean;
    userHasPermission: (permission: Permission, team?: number, project?: number | null) => boolean;

    userPic: string | null;
    setUserPic(userPic: string): void;

    reloadUserInfo: () => Promise<boolean>;

    usersTeams: TeamInfo[];

    currentTeam: TeamInfo | null;
    setCurrentTeam(team_id: number): void;

    currentTeamsProjects: {
        member_of: any[];
        not_member_of: any[];
        projects: any[];
        recent_projects: any[];
        vendor_for: any[];
    };
    reloadCurrentTeamsProjects(): Promise<void>;

    pageContentType: PageContentType | null;
    setPageContentType(pageContentType: PageContentType): void;

    /**
      Dont use this to see if the project exists, instead use currentProjectInfo.
    */
    currentProjectToken: string | null;
    setCurrentProjectToken(projectToken: string): void;

    currentProjectInfo: any | null;
    reloadCurrentProject: () => Promise<boolean>;

    currentViewedTeamInfo: TeamInfo | null;
    reloadCurrentViewedTeamInfo: () => void;

    windowSize: { width: number; height: number };
    devicePixelRatio: number | undefined;
    isMobile: boolean;

    cableApp: Cable | null;

    mapIcons: {
        equipmentIcons: string[];
        sdfIcons: string[];
        letterIcons: string[];
        numberIcons: string[];
    };

    currentProjectWeather: any | null;
    getCurrentProjectWeather: (unique_token: string) => void;

    currentAppVersion: null | string;
    setCurrentAppVersion(version: number): void;

    dashboardNotificationsChannel: any | null;

    newBroadcastMessage: boolean;
    setNewBroadcastMessage(newBroadcastMessage: boolean): void;

    mapChannel: any | null;

    setUserTheme(theme: string): void;

    activeSidebarKey: string;
    setActiveSidebarKey(activeSidebarKey: string): void;

    collapseSidebar: boolean;
    setCollapseSidebar(collapseSidebar: boolean): void;

    addTeamToProjectModal: string | null;
    setAddTeamToProjectModal(show: "member" | "vendor" | null): void;

    showNotificationsDrawer: boolean;
    setShowNotificationsDrawer(showNotificationsDrawer: boolean): void;

    projectInfo: (projectId: string | number | null) => any | null;

    mapObjectsByProject: { unique_token: string; mapObjects: MapObject[] }[];
    mapObjectsForProject: (projectId: string | number) => MapObject[];
    refreshMapObjectsByProject: () => Promise<void>;

    mapAreasByProject: { unique_token: string; areas: Area[] }[];
    mapAreasForProject: (projectId: string | number) => Area[];
    refreshMapAreasByProject: () => Promise<void>;

    equipmentByProject: { unique_token: string; equipment: Equipment[] }[];
    equipmentForProject: (projectId: string | number) => Equipment[];
    refreshEquipmentByProject: () => Promise<void>;

    dropoffsByProject: { unique_token: string; dropoffs: Dropoff[] }[];
    dropoffsForProject: (projectId: string | number) => Dropoff[];
    refreshDropoffsByProject: () => Promise<void>;
    gatesByProject: { unique_token: string; gates: Gate[] }[];
    gatesForProject: (projectId: string | number) => Gate[];
    refreshGatesByProject: () => Promise<void>;

    haulRoutesByProject: { unique_token: string; haulRoutes: HaulRoute[] }[];
    haulRoutesForProject: (projectId: string | number) => HaulRoute[];
    refreshHaulRoutesByProject: () => Promise<void>;

    gateToDropoffRoutesByProject: { unique_token: string; gateToDropoffRoutes: GateToDropoffRoute[] }[];
    gateToDropoffRoutesForProject: (projectId: string | number) => GateToDropoffRoute[];
    refreshGateToDropoffRoutesByProject: () => Promise<void>;

    dropoffGatesByProject: { unique_token: string; dropoffGates: DropoffGate[] }[];
    dropoffGatesForProject: (projectId: string | number) => DropoffGate[];
    refreshDropoffGatesByProject: () => Promise<void>;

    teamsByProject: { unique_token: string; teams: TeamInfo[] }[];
    teamsForProject: (projectId: string | number) => TeamInfo[];
    refreshTeamsByProject: () => void;

    vendorsByProject: { unique_token: string; vendorTeams: TeamInfo[] }[];
    vendorsForProject: (projectId: string | number) => TeamInfo[];
    refreshVendorsByProject: () => void;

    createDelivery: (delivery: Delivery) => Promise<Delivery | null>;
    updateDelivery: (delivery: Delivery) => Promise<Delivery | null>;
    deleteDelivery: (delivery: Delivery) => Promise<boolean>;

    mapRef: React.RefObject<MapRef | null>;
    mapGeoJson: FeatureCollection | null;
    setMapGeoJson: (mapGeoJson: FeatureCollection | null) => void;

    mapPopup: mapboxgl.Popup;

    mapBoxDraw: any;
    setMapBoxDraw: (mapBoxDraw: any) => void;

    currentMappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute | null;
    setCurrentMappable: (mapObject: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute | null) => void;

    currentMapObject: MapObject | null;
    setCurrentMapObject: (mapObject: MapObject | null) => void;

    editCurrentMappable: boolean;
    setEditCurrentMappable: (edit: boolean) => void;
    showOtherMapItemsWhileEditing: boolean;
    setShowOtherMapItemsWhileEditing: (show: boolean) => void;
    showGeotiff: boolean;
    setShowGeotiff: (show: boolean) => void;

    newMappableType: "dropoff" | "location" | "equipment" | "gate" | "haul_route" | "gate_to_dropoff_route" | null;
    setNewMappableType: (
        type: "dropoff" | "location" | "equipment" | "gate" | "haul_route" | "gate_to_dropoff_route" | null
    ) => void;

    pendingGateToDropoffRoute: PendingGateToDropoffRoute | null;
    setPendingGateToDropoffRoute: (pending: PendingGateToDropoffRoute | null) => void;

    editCurrentMappablePosition: boolean;
    setEditCurrentMappablePosition: (edit: boolean) => void;

    settingJobsiteCoordinates: boolean;
    setSettingJobsiteCoordinates: (editing: boolean) => void;

    mapFilterDate: Dayjs | null;
    setMapFilterDate: (date: Dayjs | null) => void;

    currentWaypointStep: WaypointStep | null;
    setCurrentWaypointStep: (step: WaypointStep | null) => void;

    hoveredMapObject: MapObject | null;
    setHoveredMapObject: (mapObject: MapObject | null) => void;

    hoveredDropoffGatePair: HoveredDropoffGatePair | null;
    setHoveredDropoffGatePair: (pair: HoveredDropoffGatePair | null) => void;

    mapBounds: mapboxgl.LngLatBounds;

    darkMode: boolean;

    userLocation: GeolocationCoordinates | null;
    setUserLocation: (location: GeolocationCoordinates | null) => void;
    geoControlRef: React.RefObject<mapboxgl.GeolocateControl | null>;
    waypointGuidance: boolean;
    setWaypointGuidance: (active: boolean) => void;

    calendarEventsFilterInfo: CalendarEventsFilterInfo;
    setCalendarEventsFilterInfo: (calendarEventsFilterInfo: CalendarEventsFilterInfo) => void;
    refreshCalendarEventsByProject: () => Promise<void>;
    calendarEventsByProject: { unique_token: string; calendarEvents: CalendarEvent[] }[];
    calendarEventsForProject: (projectId: string | number) => CalendarEvent[];
    createCalendarEvent: (calendarEvent: CalendarEvent) => Promise<CalendarEvent | null>;
    updateCalendarEvent: (calendarEvent: CalendarEvent) => Promise<CalendarEvent | null>;
    deleteCalendarEvent: (calendarEvent: CalendarEvent) => Promise<boolean>;
};

// @ts-expect-error: This throws an error that null cannot initialize this type. This is true, but first use will give this a real value.
const GlobalStoreContext = React.createContext<GlobalStoreContextType>(null);

export default GlobalStoreContext;
